import { FunctionComponent, useContext } from 'react'
import styled, { css } from 'styled-components'
import contextToiTeaser from '@contexts/toiTeaser'
import { TOITeaserLayoutContextType } from '@contexts/toiTeaser'
import { TextLineProps } from '@widgets/Teaser/toi/types'
import { layout, desktopCSS, mobileCSS, tabletCSS } from '@measures/responsive'

interface StyledTextLineProps
  extends Pick<
      TextLineProps,
      'isInNewsticker' | 'hasBulletpoints' | 'hasNewsticker'
    >,
    Pick<
      TOITeaserLayoutContextType,
      'accentColor' | 'articleType' | 'isInMegaModule'
    > {
  alignmentV: TOITeaserLayoutContextType['mainTextAlignment']['v']
  alignmentH: TOITeaserLayoutContextType['mainTextAlignment']['h']
}

const {
  desktop: { minWidth: minWidthDesktop },
  tablet: { minWidth: minWidthTablet },
} = layout

const { TOITeaserLayoutContext } = contextToiTeaser

const StyledTextLine = styled.div<StyledTextLineProps>`
  ${({
    hasBulletpoints,
    accentColor,
    alignmentV,
    alignmentH,
    articleType,
    isInNewsticker,
    isInMegaModule,
    hasNewsticker,
  }) => {
    const cornerPaddingDesktop = `${Math.round(
      (1.6 / 100) * minWidthDesktop
    )}px`
    const cornerPaddingTablet = `${Math.round((1.6 / 100) * minWidthTablet)}px`
    const cornerPaddingMobile = '3.7vw'

    const basicCSS = css`
      width: 8px;
      position: absolute;
      top: 0;
      background-color: ${accentColor};
    `

    if (articleType !== 'default') {
      return css`
        ${basicCSS};
        height: 100%;
        left: 0;
      `
    } else {
      if (hasBulletpoints) {
        return css`
          ${basicCSS};
          ${alignmentH}: 0;

          ${desktopCSS(css`
            height: calc(100% + ${cornerPaddingDesktop});
          `)}

          ${tabletCSS(css`
            height: calc(100% + ${cornerPaddingTablet});
          `)}

          ${mobileCSS(css`
            height: calc(100% + ${cornerPaddingMobile});
          `)}
        `
      }

      if (isInMegaModule) {
        if (isInNewsticker) {
          return css`
            ${basicCSS};
            height: 100%;
            ${alignmentH}: 0;
          `
        }

        if (alignmentH === 'right') {
          return css`
            ${basicCSS};
            height: 100%;
            ${alignmentH}: 0;
          `
        }

        if (alignmentV === 'top') {
          return css`
            ${basicCSS};
            height: calc(100% + 1000px);
            ${alignmentH}: 0;
          `
        }

        return css`
          ${basicCSS};
          ${alignmentH}: 0;

          ${desktopCSS(css`
            height: calc(100% + ${cornerPaddingDesktop});
          `)}

          ${tabletCSS(css`
            height: calc(100% + ${cornerPaddingTablet});
          `)}

          ${mobileCSS(css`
            height: calc(100% + ${cornerPaddingMobile});
          `)}
        `
      }

      return css`
        ${basicCSS};
        height: 100%;
        ${alignmentH}: 0;
        ${mobileCSS(css`
          ${hasNewsticker &&
          css`
            height: calc(100% + ${cornerPaddingMobile});
          `};
        `)}
      `
    }
  }};
`

const TextLine: FunctionComponent<TextLineProps> = ({
  hasBulletpoints,
  isInNewsticker,
  hasNewsticker,
}) => {
  const { accentColor, mainTextAlignment, articleType, isInMegaModule } =
    useContext(TOITeaserLayoutContext)

  return (
    <StyledTextLine
      hasBulletpoints={hasBulletpoints}
      isInMegaModule={isInMegaModule}
      isInNewsticker={isInNewsticker}
      hasNewsticker={hasNewsticker}
      alignmentV={mainTextAlignment['v']}
      alignmentH={mainTextAlignment['h']}
      accentColor={accentColor}
      articleType={articleType}
    />
  )
}

export default TextLine
